import React, { useRef } from 'react';
import { Stack, Box, Collapse, Grid, Typography, TextField } from '@mui/material';
import { ConfigSwitchSection } from '../../../common';
import {
  ShopperCredentialSharingValue,
  PaymentCallback,
} from '../../../store/modules/merchant/types';
import { useMainContent } from '../../../contexts/MainContentContext';

export type PaymentCallbackErrors = {
  url: string | null;
  headerKey: string | null;
  headerValue: string | null;
};

interface Props {
  isLoading?: boolean;
  disabled?: boolean;
  shopperCredentialSharingValue?: ShopperCredentialSharingValue;
  onShopperCredentialSharingChange: (
    shopperCredentialSharingEnabled: boolean,
    shopperCredentialSharingValue: ShopperCredentialSharingValue,
  ) => void;
  callback: PaymentCallback;
  onCallbackChange: (callback: PaymentCallback) => void;
  callbackErrors: PaymentCallbackErrors;
}

export const PaymentCredentialSharing = ({
  isLoading = false,
  disabled = true,
  shopperCredentialSharingValue,
  onShopperCredentialSharingChange,
  callback,
  onCallbackChange,
  callbackErrors,
}: Props) => {
  const textFieldRef = useRef<HTMLDivElement>(null);
  const headerSectionRef = useRef<HTMLDivElement>(null);
  const isAnySharingEnabled =
    shopperCredentialSharingValue?.panSharingEnabled ||
    shopperCredentialSharingValue?.networkTokenSharingEnabled;
  const { mainContentRef } = useMainContent();

  const scrollToTextField = () => {
    setTimeout(() => {
      if (headerSectionRef.current && mainContentRef.current) {
        // Use the custom header section as the target for scrolling
        // This ensures both callback URL and custom header are visible
        const headerSectionPosition = headerSectionRef.current.offsetTop;

        // Adjust the scroll position to show both callback URL and header fields
        // The additional offset ensures the header section is fully visible
        const scrollTarget = headerSectionPosition - mainContentRef.current.clientHeight + 250;

        mainContentRef.current.scrollTo({
          top: scrollTarget,
          behavior: 'smooth',
        });
      }
    }, 350); // Let the collapse animation finish before scrolling
  };

  const handleCredentialChange = (name: keyof ShopperCredentialSharingValue, value: boolean) => {
    // Check if any sharing was previously enabled
    const wasSharingEnabled =
      shopperCredentialSharingValue?.panSharingEnabled ||
      shopperCredentialSharingValue?.networkTokenSharingEnabled;

    // Create the updated sharing values
    const updatedValues = {
      panSharingEnabled:
        name === 'panSharingEnabled' ? value : !!shopperCredentialSharingValue?.panSharingEnabled,
      networkTokenSharingEnabled:
        name === 'networkTokenSharingEnabled'
          ? value
          : !!shopperCredentialSharingValue?.networkTokenSharingEnabled,
    };

    // Call the handler with updated values
    onShopperCredentialSharingChange(true, updatedValues);

    // Only scroll if we're going from no sharing to some sharing enabled
    if (
      !wasSharingEnabled &&
      (updatedValues.panSharingEnabled || updatedValues.networkTokenSharingEnabled)
    ) {
      scrollToTextField();
    }
  };

  const handleCallbackUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCallbackChange({
      ...callback,
      url: e.target.value,
    });
  };

  const handleHeaderKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCallbackChange({
      ...callback,
      headerKey: e.target.value,
    });
  };

  const handleHeaderValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCallbackChange({
      ...callback,
      headerValue: e.target.value,
    });
  };

  return (
    <Stack
      sx={{
        maxWidth: '450px',
        mb: isAnySharingEnabled ? 3 : 0,
      }}
    >
      <Box sx={{ mt: 2 }}>
        <ConfigSwitchSection
          title="Payment Credential Sharing"
          description="Manage who gets access to your payment details. Enable or disable sharing of PAN and network tokens for enhanced privacy and control."
          isChecked={shopperCredentialSharingValue?.panSharingEnabled ?? false}
          onToggle={(isChecked) => handleCredentialChange('panSharingEnabled', isChecked)}
          isLoading={isLoading}
          switchLabelTitle="PAN Sharing"
          disabled={disabled}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <ConfigSwitchSection
          isChecked={shopperCredentialSharingValue?.networkTokenSharingEnabled ?? false}
          onToggle={(isChecked) => handleCredentialChange('networkTokenSharingEnabled', isChecked)}
          isLoading={isLoading}
          switchLabelTitle="Network Token Sharing"
          disabled={disabled}
        />
      </Box>

      {/* Display helper text when no sharing is enabled */}
      {!isAnySharingEnabled && (
        <Typography
          sx={{
            fontSize: '12px',
            fontStyle: 'italic',
            color: 'text.secondary',
            mt: 1,
            mb: 2,
          }}
        >
          Enable either PAN Sharing or Network Token Sharing to configure callback settings.
        </Typography>
      )}

      <Collapse in={isAnySharingEnabled}>
        <Box sx={{ mt: 3 }} ref={textFieldRef}>
          <TextField
            fullWidth
            label="Callback URL"
            value={callback.url}
            onChange={handleCallbackUrlChange}
            disabled={disabled || isLoading}
            placeholder="Please enter URL"
            helperText={callbackErrors.url || 'Required when sharing is enabled'}
            error={!!callbackErrors.url}
            required
          />
        </Box>
        <Box
          sx={{
            mt: 3,
          }}
          ref={headerSectionRef}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              mb: 1,
            }}
          >
            Custom Header
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key"
                value={callback.headerKey || ''}
                onChange={handleHeaderKeyChange}
                disabled={disabled || isLoading}
                placeholder="X-Custom-Header"
                helperText={callbackErrors.headerKey}
                error={!!callbackErrors.headerKey}
                inputProps={{ maxLength: 128 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Value"
                value={callback.headerValue || ''}
                onChange={handleHeaderValueChange}
                disabled={disabled || isLoading}
                placeholder="Bearer token123"
                helperText={callbackErrors.headerValue}
                error={!!callbackErrors.headerValue}
                inputProps={{ maxLength: 8192 }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Stack>
  );
};
